import BackspaceIcon from "@mui/icons-material/Backspace";
import EditIcon from "@mui/icons-material/Edit";
import {PuiDefaultPage, PuiFormikForm, PuiGrid} from "@portnet/ui";
import {Formik} from "formik";
import React from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import AppSimpleReferentielField from "../../../../common/components/inputs/AppSimpleReferentielField";
import AppTextField from "../../../../common/components/inputs/AppTextField";
import AppButton from "../../../../common/components/ui/AppButton";
import AppSection from "../../../../common/components/ui/AppSection";
import {GESTION_PACKAGES_PAGE_ROUTE} from "../../../../common/constants/routerConstants";
import * as modificationPackageFormNames from "../others/modificationPackageFormNames";
import {modificationPackageInitialValues} from "../others/modificationPackageInitialValues";
import {modificationPackageValidationSchema} from "../others/modificationPackageValidationSchemas";
import {
    getPackageToModifyAction,
    modificationPackageAction,
    modificationPackageReinitializationAction,
} from "../state/modificationPackageActions";

const NavigationActions = ({ formRef }) => {
  return (
    <PuiGrid container>
      <PuiGrid item>
        <AppButton
          color="tertiary"
          startIcon={<BackspaceIcon />}
          onClick={() => {
            formRef.current.resetForm();
          }}
        >
          Effacer
        </AppButton>
      </PuiGrid>
      <PuiGrid item>
        <AppButton
          color="primary"
          startIcon={<EditIcon />}
          onClick={() => {
            formRef.current.submitForm();
          }}
        >
          Modifier
        </AppButton>
      </PuiGrid>
    </PuiGrid>
  );
};

const ModificationPackagePage = () => {
  const navigate = useNavigate();

  const { packageId } = useParams();

  const dispatch = useDispatch();

  const formRef = React.useRef();

  const {
    isLoading: isModificationPackageLoading,
    isSuccessed: isModificationPackageSuccessed,
    hasError: hasModificationPackageError,
  } = useSelector(
    (state) => state.packages.modificationPackage.envoiModificationPackage
  );

  const {
    content: packageToModify,
    isLoading: isPackageToModifyLoading,
    isSuccessed: isPackageToModifySuccessed,
  } = useSelector(
    (state) => state.packages.modificationPackage.packageToModify
  );

  const submitHandler = (values) => {
    dispatch(modificationPackageAction({ packageId, data: values }));
  };

  React.useEffect(() => {
    dispatch(getPackageToModifyAction(packageId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId]);

  React.useEffect(() => {
    if (hasModificationPackageError) {
      dispatch(modificationPackageReinitializationAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasModificationPackageError]);

  React.useEffect(() => {
    if (isModificationPackageSuccessed) {
      navigate(GESTION_PACKAGES_PAGE_ROUTE);
      dispatch(modificationPackageReinitializationAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModificationPackageSuccessed]);

  return (
    <PuiDefaultPage
      title="Modification package"
      titleIcon={<EditIcon />}
      trace={["Accueil", "Modification package"]}
      navActions={<NavigationActions formRef={formRef} />}
      loading={isModificationPackageLoading || isPackageToModifyLoading}
      show={isPackageToModifySuccessed}
      topNav
      bottomNav
      retour
      onRetour={() => navigate(GESTION_PACKAGES_PAGE_ROUTE)}
    >
      {isPackageToModifySuccessed && (
        <AppSection title="Modification package">
          <Formik
            initialValues={modificationPackageInitialValues}
            validationSchema={modificationPackageValidationSchema}
            onSubmit={submitHandler}
            innerRef={formRef}
          >
            <PuiFormikForm>
              <PuiGrid container>
                <PuiGrid item xs={12} sm={4} md={4}>
                  <AppTextField
                    formik={false}
                    label="Rôle de l'opérateur"
                    value={packageToModify.roleOperateur.description}
                    disabled
                  />
                </PuiGrid>
                <PuiGrid item xs={12} sm={4} md={4}>
                  <AppTextField
                    formik={false}
                    label="Critère de facturation"
                    value={packageToModify.critereFacturation.description}
                    disabled
                  />
                </PuiGrid>
                <PuiGrid item xs={12} sm={4} md={4}>
                  <AppTextField
                    formik={false}
                    label="Paramètre de décompte"
                    value={packageToModify.parametreDecompte.description}
                    disabled
                  />
                </PuiGrid>
                <PuiGrid item xs={12} sm={4} md={4}>
                  <AppTextField
                    formik={false}
                    label="Type de paiement"
                    value={packageToModify.typePaiment.description}
                    disabled
                  />
                </PuiGrid>
                <PuiGrid item xs={12} sm={4} md={4}>
                  <AppTextField
                    formik={false}
                    label="Transaction autorisée"
                    value={packageToModify.typeTransactionAutorisee.description}
                    disabled
                  />
                </PuiGrid>
                <PuiGrid item xs={12} sm={4} md={4}>
                  <AppTextField
                    formik={false}
                    label="Nombre de transactions"
                    value={packageToModify.nbrTransaction}
                    disabled
                  />
                </PuiGrid>
                <PuiGrid item xs={12} sm={4} md={4}>
                  <AppTextField
                    formik={false}
                    label="Frais d'accès annuel en MAD (TTC)"
                    value={packageToModify.abonnementBase}
                    disabled
                  />
                </PuiGrid>
                <PuiGrid item xs={12} sm={4} md={4}>
                  <AppTextField
                    formik={false}
                    label="Début de validité"
                    value={packageToModify.debutValidite.description}
                    disabled
                  />
                </PuiGrid>
                <PuiGrid item xs={12} sm={4} md={4}>
                  <AppSimpleReferentielField
                    autocomplete
                    searchKey="LISTE_STATUS_PACKAGE"
                    label="Statut du package"
                    name={modificationPackageFormNames.STATUS_PACKAGE_FORM_NAME}
                    getOptionLabel={(option) => option.description}
                    getOptionValue={(option) => option.code}
                    filterFunc={(option) => option.code !== "DELETED"}
                    value={packageToModify.statut}
                    required
                  />
                </PuiGrid>
                <PuiGrid item xs={12} sm={4} md={4}>
                  <AppTextField
                    formik={false}
                    label="Type de frais de service"
                    value={packageToModify.typeFraisService.description}
                    disabled
                  />
                </PuiGrid>
                <PuiGrid item xs={12} sm={4} md={4}>
                  <AppTextField
                    formik={false}
                    label="Reconduction"
                    value={packageToModify.reconduction ? "Oui" : "Non"}
                    required
                    disabled
                  />
                </PuiGrid>
                <PuiGrid item xs={12} sm={4} md={4}>
                  <AppTextField
                    formik={false}
                    label="Frais transaction en MAD"
                    value={
                      packageToModify[
                        modificationPackageFormNames.FRAIS_TRANSACTION_FORM_NAME
                      ]
                    }
                    disabled
                  />
                </PuiGrid>
                <PuiGrid item xs={12}>
                  <AppTextField
                    label="Description du package"
                    name={modificationPackageFormNames.DESCRIPTION_FORM_NAME}
                    multiline
                    rows={6}
                    value={
                      packageToModify[
                        modificationPackageFormNames.DESCRIPTION_FORM_NAME
                      ]
                    }
                    required
                  />
                </PuiGrid>
              </PuiGrid>
            </PuiFormikForm>
          </Formik>
        </AppSection>
      )}
    </PuiDefaultPage>
  );
};

export default ModificationPackagePage;
